import React from "react";
import { useTranslation } from "react-i18next";
import styles from "./ArtifactViewer.module.css";
import ClipLoader from "react-spinners/ClipLoader";
import ElementsContextType from "../../types/ElementsContextType";
import { ElementsContext } from "../contexts/ElementsContext";
import downloadFile from "../../services/DownloadFile";
import DeleteModal from "../DeleteView/DeleteModal";
import { useDispatch, useSelector } from "react-redux";
import { AssetType } from "../../types/AssetType";
import ElementAsset from "../../types/ElementAsset";
import { toast } from "react-toastify";
import { SystemMessage } from "../../config/SystemMessage";
import { addCommentAction } from "../../store/actions/Asset";
import ReactGA from "react-ga4";
import { LoggedInUserType } from "../../types/LoggedInUserType";

interface Props {
    focalImage: ElementAsset;
    dataId: string;
    type: AssetType;
}

export default function ArtifactViewer({ dataId, focalImage, type }: Props) {
    const { closeImageViewer } = React.useContext(
        ElementsContext
    ) as ElementsContextType;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const localUpdateTime = useSelector(
        (state: any) => state.portal.activeInspection?.localUpdateTime
    );
    const [currentImage, setCurrentImage] = React.useState<any>();
    const [loaded, setLoaded] = React.useState(false);
    const [index, setIndex] = React.useState(0);
    const [showDeleteModal, setShowDeleteModal] = React.useState(false);
    const [comment, setComment] = React.useState<string>("");

    const dispatch = useDispatch();

    const { t } = useTranslation("common");

    const currentInspection = useSelector(
        (state: any) => state.portal.activeInspection
    );
    const sectionIndex = currentInspection.sections.findIndex(
        (element: any) => element.Id === dataId
    );
    const data = currentInspection.sections[sectionIndex];

    let sectionComplete = false;

    if (type === AssetType.Document) {
        sectionComplete =
            currentInspection.SurveyorCompletionStatus.DocumentsComplete;
    }
    if (type === AssetType.Image) {
        sectionComplete =
            currentInspection.SurveyorCompletionStatus.PhotosComplete;
    }
    if (type === AssetType.CheckList) {
        sectionComplete =
            currentInspection.SurveyorCompletionStatus.ChecklistComplete;
    }

    const deleteSvg: string = sectionComplete
        ? "/images/Roll-Over-Delete-Disable.svg"
        : "/images/Delete.svg";
    const cursorStyle: string = sectionComplete ? "default" : "pointer";
    const submitText: string = sectionComplete ? "Submit Disabled" : "Submit";
    const submitImage: string = sectionComplete
        ? "/images/Submit-Disabled.svg"
        : "/images/Submit.svg";
    const submitColor: string = sectionComplete ? "#B4B4B4" : "#3A56AF";

    const userType: LoggedInUserType = useSelector((state: any) => state.portal.userType);

    if (
        (type === AssetType.CheckList && data.checkList.assets.length === 0) ||
        (type === AssetType.Image && data.Images.length === 0) ||
        (type === AssetType.Document && data.Documents.length === 0)
    ) {
        closeImageViewer();
    } else if (
        type === AssetType.Image &&
        currentImage &&
        currentImage.id !== data.Images[index].id
    ) {
        setCurrentImage(data.Images[index]);
    } else if (
        type === AssetType.Document &&
        currentImage &&
        currentImage.id !== data.Documents[index].id
    ) {
        data.Documents[index].downloadUrl = data.Documents[index].url;
        data.Documents[index].url = "/images/document.svg";
        setCurrentImage(data.Images[index]);
    }

    React.useEffect(() => {
        if (type === AssetType.Document) {
            data.Documents.forEach((element: any) => {
                element.downloadUrl = element.url;
                element.url = "/images/document.svg";
            });
            data.Images = data.Documents; // docs now shown as images
        }
        // // Remove focus image from array
        data.Images = data.Images.filter(function (image: any) {
            return image.name !== focalImage.name;
        });
        // // Add focus image to the front of array
        data.Images.unshift(focalImage);
        setLoaded(true);
    }, []);

    function next() {
        if (data.Images.length > 1) {
            setLoaded(false);
            let viewerIndex = index;
            if (viewerIndex === data.Images.length - 1) {
                viewerIndex = 0;
            } else {
                viewerIndex++;
            }
            setIndex(viewerIndex);
        }
    }

    function previous() {
        if (data.Images.length > 1) {
            setLoaded(false);
            if (index !== 0) {
                setIndex(index - 1);
            } else {
                setIndex(data.Images.length - 1);
            }
        }
    }

    async function deleteAsset() {
        if (!sectionComplete) {
            setShowDeleteModal(true);
        }
    }

    function download() {
        let url = currentImage?.url;
        const name = currentImage?.name;

        if (type === AssetType.Document) {
            url = currentImage?.downloadUrl;
        }

        if (url && name) {
            return downloadFile(url, name);
        }

        console.error("cannot download the selected image");
    }

    async function submitComment() {
        if (!sectionComplete) {
            const asset = currentImage;
            try {
                ReactGA.event({
                    category: "Asset Comment",
                    action: "adding",
                    label: comment,
                    value: 0
                });
                await dispatch(
                    addCommentAction(
                        currentInspection.Id,
                        data.Id,
                        asset.id,
                        type,
                        "comments",
                        comment
                    )
                );
                toast.success(t(SystemMessage.COMMENT_ADDED));
                ReactGA.event({
                    category: "Asset Comment",
                    action: "added",
                    label: comment,
                    value: 0
                });
            } catch (e) {
                console.error(e);
                toast.error(t(SystemMessage.COMMENT_FAILED));
                ReactGA.event({
                    category: "Asset Comment",
                    action: "failed",
                    label: comment,
                    value: 0
                });
            }
        }
    }

    React.useEffect(() => {
        if (type === AssetType.Document) {
            data.Documents.forEach((element: any) => {
                element.url = "/images/document.svg";
            });
            data.Images = data.Documents; // docs now shown as images
        }
        const current = data.Images[index];
        setCurrentImage(current);
        if (current) {
            if (current.metadata) {
                if (current.metadata.comments) {
                    setComment(current.metadata.comments);
                } else {
                    setComment("");
                }
            }
        }
        const img = new Image();
        if (type === AssetType.Document) {
            img.src = "/images/document.svg";
        } else {
            img.src = data.Images[index]["url"];
        }
        img.onload = () => {
            setLoaded(true);
        };
    }, [index, data.Images, data.Documents]);

    function handleCommentField(event: any) {
        setComment(event.target.value);
    }

    return (
        <div className={styles.viewerWrapper}>
            {currentImage ? (
                <DeleteModal
                    elementId={data.Id}
                    asset={currentImage}
                    active={showDeleteModal}
                    closeModal={() => {
                        setShowDeleteModal(false);
                    }}
                />
            ) : null}

            <header>
                <div className={styles.elementTitle}>
                    {type === AssetType.Image ? "Photos" : "Documents"}:{" "}
                    {data.Name}
                </div>
                <div>
                    <img
                        alt="Download"
                        width="20px"
                        height="20px"
                        src="/images/Download.svg"
                        onClick={download}
                    />
                    <img
                        alt="Delete"
                        width="20px"
                        height="20px"
                        src={deleteSvg}
                        onClick={deleteAsset}
                        style={{ cursor: cursorStyle }}
                    />
                    <img
                        alt="Close"
                        className={styles.close}
                        width="20px"
                        height="20px"
                        src="/images/Close.svg"
                        onClick={() => closeImageViewer()}
                    />
                </div>
            </header>

            <section>
                <article>
                    {loaded ? (
                        type === AssetType.Image &&
                        data.Images[index] !== undefined ? (
                            <div
                                className={styles.viewerImage}
                                style={{
                                    backgroundImage: `url(${data.Images[index]["url"]})`
                                }}
                            />
                        ) : (
                            <div className={styles.viewerDoc}>
                                No Preview Available
                            </div>
                        )
                    ) : (
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "center",
                                alignContent: "center",
                                alignItems: "center",
                                backgroundColor: "#B4B4B4"
                            }}
                            className={styles.viewerImage}
                        >
                            <ClipLoader
                                color={"#ffffff"}
                                loading={true}
                                size={"50px"}
                            />
                        </div>
                    )}

                    <footer className={styles.artifactViewerFooter}>
                        <div>
                            <img
                                alt="Previous"
                                width="20px"
                                height="20px"
                                src="/images/Back-Arrow.svg"
                                onClick={previous}
                            />
                            <span>
                                {index + 1} / {data.Images.length}{" "}
                            </span>
                            <img
                                alt="Next"
                                width="20px"
                                height="20px"
                                src="/images/Next-Arrow.svg"
                                onClick={next}
                            />
                        </div>
                    </footer>
                </article>

                <aside>
                    {currentImage ? (
                        <div>
                            <p>{currentImage.name}</p>
                            <section>
                                {(type === AssetType.Document ||
                                    AssetType.CheckList) && (
                                    <div>
                                        <div className={styles.box}>
                                            <div className={styles.sideTitle}>
                                                Upload Date
                                            </div>
                                            <div className={styles.sideData}>
                                                {currentImage.metadata[
                                                    "added_on"
                                                ]
                                                    ? new Date(
                                                          currentImage.metadata[
                                                              "added_on"
                                                          ]
                                                      ).toLocaleString(
                                                          "en-GB",
                                                          {
                                                              timeZone: "UTC",
                                                              year: "numeric",
                                                              month: "long",
                                                              day: "numeric"
                                                          }
                                                      ) + " (UTC)"
                                                    : "No Upload Date"}
                                            </div>
                                        </div>

                                        <div className={styles.box}>
                                            <div className={styles.sideTitle}>
                                                Upload By
                                            </div>
                                            <div className={styles.sideData}>
                                                {currentImage.metadata["asset-uploaded-by"] ?? (userType === LoggedInUserType.DOCUMENT_COLLECTOR ? t("users.vesselContact") : userType === LoggedInUserType.IDWAL_ADMIN ? "Idwal" : t("users.surveyor"))}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div className={styles.sideTitle}>Comments</div>
                                <div className={styles.commentBox}>
                                    <textarea
                                        placeholder="Comments"
                                        value={comment}
                                        onChange={handleCommentField}
                                        disabled={sectionComplete}
                                    />
                                    <div
                                        onClick={submitComment}
                                        className={styles.commentActions}
                                    >
                                        <p
                                            style={{
                                                color: submitColor,
                                                cursor: cursorStyle
                                            }}
                                        >
                                            {submitText}
                                        </p>
                                        <img
                                            alt="Submit"
                                            width="16px"
                                            height="14px"
                                            src={submitImage}
                                            style={{ cursor: cursorStyle }}
                                        />
                                    </div>
                                </div>
                            </section>
                        </div>
                    ) : null}
                </aside>
            </section>
        </div>
    );
}
